import { useState } from "react";
import { toast, Toaster } from "sonner";
import { motion } from "framer-motion";
import { RevealLines, RevealWords } from "@/components/RevealText";
import { Reveal } from "@/components/Reveal";
import EditorialMarquee from "@/components/EditorialMarquee";
import { INQUIRY } from "@/constants/testIds";
import { submitInquiry } from "@/lib/api";
import { ArrowRight, Check } from "lucide-react";

const interestOptions = [
  { value: "bulls", label: "Bulls" },
  { value: "replacement-heifers", label: "Replacement Heifers" },
  { value: "seedstock", label: "Seedstock" },
  { value: "general", label: "General" },
];

export default function Contact() {
  const [form, setForm] = useState({
    name: "",
    email: "",
    phone: "",
    interest: "general",
    message: "",
  });
  const [submitting, setSubmitting] = useState(false);
  const [done, setDone] = useState(false);

  const onChange = (e) => {
    setForm({ ...form, [e.target.name]: e.target.value });
  };

  const onSubmit = async (e) => {
    e.preventDefault();
    if (submitting) return;
    if (!form.name.trim() || !form.email.trim() || !form.message.trim()) {
      toast.error("Please fill in name, email and a short message.");
      return;
    }
    setSubmitting(true);
    try {
      await submitInquiry({
        name: form.name.trim(),
        email: form.email.trim(),
        phone: form.phone.trim() || undefined,
        interest: form.interest,
        message: form.message.trim(),
      });
      setDone(true);
      toast.success("Message received. We'll be in touch.");
      setForm({
        name: "",
        email: "",
        phone: "",
        interest: "general",
        message: "",
      });
    } catch (err) {
      console.error(err);
      const msg =
        err?.response?.data?.detail?.[0]?.msg ||
        err?.response?.data?.detail ||
        "Something went wrong sending your message.";
      toast.error(typeof msg === "string" ? msg : "Please try again.");
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="bg-bone text-ink">
      <Toaster position="top-center" richColors />
      {/* Hero */}
      <section className="relative pt-40 md:pt-48 pb-16">
        <div className="mx-auto max-w-[1600px] px-6 md:px-10 lg:px-14 grid grid-cols-12 gap-8 items-end">
          <div className="col-span-12 md:col-span-8">
            <RevealWords
              text="— Correspondence"
              className="font-mono text-[11px] tracking-overline uppercase text-sage"
            />
            <RevealLines
              as="h1"
              lines={["Write to us.", "We read every letter."]}
              className="mt-8 font-serif text-[12vw] md:text-[8.4vw] lg:text-[7.4vw] leading-[0.92] tracking-tightest text-ink"
              delay={0.2}
              stagger={0.12}
            />
          </div>
          <Reveal className="col-span-12 md:col-span-4" delay={0.35}>
            <p className="text-ink/75 leading-relaxed max-w-[42ch]">
              Whether you&apos;re looking at a bull, sourcing replacement females, or want to
              visit the ranch, we&apos;d like to hear from you.
            </p>
          </Reveal>
        </div>
      </section>

      <EditorialMarquee
        phrases={[
          "Stevensville, Montana",
          "Bitterroot Valley",
          "John & Jennifer Quinn",
          "Inquiries · Visits",
          "Angus · Charolais",
        ]}
      />

      {/* Form + Contact info */}
      <section className="relative">
        <div className="mx-auto max-w-[1600px] px-6 md:px-10 lg:px-14 py-24 md:py-32">
          <div className="grid grid-cols-12 gap-10 lg:gap-14">
            {/* Info column */}
            <div className="col-span-12 lg:col-span-4">
              <Reveal>
                <p className="font-mono text-[11px] tracking-overline uppercase text-terracotta">
                  — The Ranch
                </p>
                <h3 className="mt-4 font-serif text-4xl md:text-5xl tracking-tight text-ink leading-[1]">
                  Hideaway Cattle Co.
                </h3>
                <div className="mt-6 space-y-3 text-ink/80 font-sans text-[15px] leading-relaxed">
                  <p>John &amp; Jennifer Quinn</p>
                  <p className="text-sage">
                    3606 Bridle Bit Lane
                    <br />
                    Stevensville, MT 59870
                  </p>
                  <p className="text-sage">
                    <a
                      href="tel:+19167198522"
                      className="hover:text-terracotta transition-colors"
                      data-testid="contact-phone-link"
                    >
                      916.719.8522
                    </a>
                    <br />
                    <a
                      href="mailto:hideawaycattle@gmail.com"
                      className="hover:text-terracotta transition-colors"
                      data-testid="contact-email-link"
                    >
                      hideawaycattle@gmail.com
                    </a>
                  </p>
                </div>
              </Reveal>

              <Reveal className="mt-12" delay={0.05}>
                <p className="font-mono text-[11px] tracking-overline uppercase text-terracotta">
                  — Visits
                </p>
                <p className="mt-4 text-ink/75 leading-relaxed max-w-[42ch]">
                  Visits by appointment. The best time to see the cattle is
                  through [months here] when calves are on the ground.
                </p>
              </Reveal>
            </div>

            {/* Form column */}
            <div className="col-span-12 lg:col-span-8">
              <Reveal>
                {done ? (
                  <motion.div
                    data-testid={INQUIRY.success}
                    initial={{ opacity: 0, y: 24 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ duration: 0.7, ease: [0.19, 1, 0.22, 1] }}
                    className="border border-ink/20 bg-parchment p-10 md:p-14"
                  >
                    <span className="inline-flex items-center justify-center h-12 w-12 border border-ink/40">
                      <Check className="h-5 w-5" />
                    </span>
                    <h3 className="mt-6 font-serif text-4xl md:text-5xl tracking-tight text-ink leading-[1]">
                      Message received.
                    </h3>
                    <p className="mt-4 text-ink/75 leading-relaxed max-w-[52ch]">
                      Thank you. John or Jennifer will reach out in due course.
                      For urgent matters, please call the ranch directly.
                    </p>
                    <button
                      type="button"
                      onClick={() => setDone(false)}
                      data-testid="inquiry-new-btn"
                      className="mt-8 inline-flex items-center gap-3 border-b border-ink/60 pb-1 text-[12px] uppercase tracking-overline hover:text-terracotta hover:border-terracotta transition-colors"
                    >
                      Write another
                      <ArrowRight className="h-4 w-4" />
                    </button>
                  </motion.div>
                ) : (
                  <form
                    onSubmit={onSubmit}
                    data-testid={INQUIRY.form}
                    className="grid grid-cols-12 gap-6"
                  >
                    <Field
                      label="Name"
                      required
                      className="col-span-12 md:col-span-6"
                    >
                      <input
                        type="text"
                        name="name"
                        required
                        value={form.name}
                        onChange={onChange}
                        placeholder="Your full name"
                        data-testid={INQUIRY.name}
                        className={inputCls}
                      />
                    </Field>
                    <Field
                      label="Email"
                      required
                      className="col-span-12 md:col-span-6"
                    >
                      <input
                        type="email"
                        name="email"
                        required
                        value={form.email}
                        onChange={onChange}
                        placeholder="you@example.com"
                        data-testid={INQUIRY.email}
                        className={inputCls}
                      />
                    </Field>
                    <Field label="Phone" className="col-span-12 md:col-span-6">
                      <input
                        type="tel"
                        name="phone"
                        value={form.phone}
                        onChange={onChange}
                        placeholder="(optional)"
                        data-testid={INQUIRY.phone}
                        className={inputCls}
                      />
                    </Field>
                    <Field
                      label="I'm interested in"
                      required
                      className="col-span-12 md:col-span-6"
                    >
                      <div className="relative">
                        <select
                          name="interest"
                          value={form.interest}
                          onChange={onChange}
                          data-testid={INQUIRY.interest}
                          className={`${inputCls} appearance-none pr-10`}
                        >
                          {interestOptions.map((o) => (
                            <option key={o.value} value={o.value}>
                              {o.label}
                            </option>
                          ))}
                        </select>
                        <span className="pointer-events-none absolute inset-y-0 right-4 flex items-center text-ink/60 font-mono text-xs">
                          ▾
                        </span>
                      </div>
                    </Field>
                    <Field label="Message" required className="col-span-12">
                      <textarea
                        name="message"
                        required
                        value={form.message}
                        onChange={onChange}
                        rows={6}
                        placeholder="Tell us about your program, what you're looking for, and preferred timing."
                        data-testid={INQUIRY.message}
                        className={`${inputCls} resize-y min-h-[160px]`}
                      />
                    </Field>

                    <div className="col-span-12 flex flex-col md:flex-row md:items-center md:justify-between gap-4 pt-2">
                      <p className="font-mono text-[10px] tracking-overline uppercase text-sage">
                        We reply within 2–3 business days.
                      </p>
                      <button
                        type="submit"
                        disabled={submitting}
                        data-testid={INQUIRY.submit}
                        className="group inline-flex items-center justify-center gap-3 px-8 py-4 bg-ink text-bone text-[12px] uppercase tracking-overline hover:bg-terracotta transition-colors disabled:opacity-60 disabled:cursor-not-allowed"
                      >
                        {submitting ? "Sending…" : "Send inquiry"}
                        <ArrowRight className="h-4 w-4 transition-transform group-hover:translate-x-1" />
                      </button>
                    </div>
                  </form>
                )}
              </Reveal>
            </div>
          </div>
        </div>
      </section>
    </div>
  );
}

const inputCls =
  "w-full bg-bone border border-ink/25 focus:border-ink text-ink placeholder-ink/40 font-sans text-[15px] px-4 py-3.5 outline-none transition-colors";

function Field({ label, required, className = "", children }) {
  return (
    <label className={`block ${className}`}>
      <span className="block font-mono text-[10px] tracking-overline uppercase text-sage mb-2">
        {label} {required && <span className="text-terracotta">*</span>}
      </span>
      {children}
    </label>
  );
}
